import React, { useContext, useEffect, useMemo, useState } from 'react';
import classes from './CharacterCounter.module.scss';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { calculateLinkLengthPerProfile, getCharacterLimit, getTextLength } from './CharacterCounter.helpers';
import { ToastMessage } from 'primereact/toast';
import { showToast } from '../../../../helpers/showToast';
import classNames from 'classnames';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { hasLinksChecker } from '../helpers/withLinks';
import { getSelectedProfilesWithBoards } from '../../helpers/getSelectedProfilesWithBoards';
import { mapSocialProfileWithBoardsAndMentionToSelectedValue } from '../../helpers/mapSelectedProfilesValues';
import { Tooltip } from '../../../../components/actions/Tooltip';
import { idGenerator } from 'helpers/idGenerator';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';

export type CharacterCounterViewProps = {
  //
  variationId?: number;
};

export const CharacterCounterView = (props: CharacterCounterViewProps): JSX.Element => {
  const { variationId } = props;
  const [showError, setShowError] = useState<false | ToastMessage>(false);
  const { state: postEditorState, profiles: availableProfiles } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const postGroup = postEditorState.postGroups[postGroupIndex];
  const { customizeContentPerProfile, posts, bitlyAccount } = postGroup;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const isCustomizing = customizeContentPerProfile && typeof customizeIndex === 'number';
  const link = isCustomizing ? posts[customizeIndex].content.link : postGroup.groupPostAttributes.content.link;
  const text = isCustomizing
    ? variationId
      ? posts[customizeIndex].content.texts.find(text => text.id === variationId)?.text || []
      : posts[customizeIndex].content.firstComment
    : variationId
    ? postGroup.groupPostAttributes.content.texts.find(text => text.id === variationId)?.text || []
    : postGroup.groupPostAttributes.content.firstComment;
  const hasLinks = hasLinksChecker(text);
  const isFirstComment = isCustomizing
    ? !variationId && posts[customizeIndex].content.shouldShowFirstComment
    : !variationId && postGroup.groupPostAttributes.content.shouldShowFirstComment;
  const selectedProfiles = getSelectedProfilesWithBoards(
    postGroup.profiles,
    availableProfiles,
    customizeIndex,
    customizeContentPerProfile,
    posts
  );
  const fbProfiles = selectedProfiles.filter(p => p.__typename === 'FacebookSocialProfile');
  const onlyFbIncluded = fbProfiles.length && fbProfiles.length === selectedProfiles.length;
  const charLimit = getCharacterLimit(selectedProfiles, isFirstComment);

  useEffect(() => {
    if (showError) {
      showToast({ severity: showError.severity, summary: showError.summary, detail: showError.detail });
      setShowError(false);
    }
  }, [showError]);

  let totalLength = getTextLength(text);
  if (hasLinks && link && bitlyAccount) {
    // check if customize per profile is on
    const { profilesWithBitly, defaultBitly } = bitlyAccount;
    const mappedSelectedProfiles = mapSocialProfileWithBoardsAndMentionToSelectedValue(selectedProfiles);
    let longestLink = mappedSelectedProfiles.reduce((prev, current) => {
      const bitlyProfile = profilesWithBitly.find(p => p.id === current.profileId);
      if (!bitlyProfile) return prev;
      const value = calculateLinkLengthPerProfile(link, bitlyProfile, defaultBitly, postGroup.categoryId);
      return Math.max(prev, value);
    }, 0);
    // if we have TW profile as selected, than we can't get the longest link, since TW always have 23 chars long links, so we should use that instead.
    if (mappedSelectedProfiles.some(p => p.__typename === 'TwitterSocialProfile')) {
      longestLink = 23;
    }
    totalLength += longestLink;
  }
  const isOffLimit = charLimit && totalLength > charLimit;
  const uniqId = useMemo(() => idGenerator('charCount'), []);
  if (!charLimit || (!isOffLimit && onlyFbIncluded)) return <></>;

  const counterClasses = classNames(classes.CharacterCounter, {
    [classes.OffLimit]: isOffLimit,
    [classes.CloseToLimit]: charLimit && totalLength <= charLimit && totalLength > charLimit - 20
  });

  return (
    <>
      <Tooltip
        target={`#charCounter${uniqId}`}
        type={'regular'}
        autoZIndex={false}
        style={{ zIndex: scssVars['z-index-overmodal'] }}>
        <div>
          <p className={'no-b-m small-text'}>Remaining characters (for the shortest profile selected).</p>
          <p className={'no-b-m small-text'}>
            You can have different amounts of text for each profile when you Customize per Profile. This allows you to
            have long text for LinkedIn and short text for X/Twitter.
          </p>
        </div>
      </Tooltip>
      <div id={`charCounter${uniqId}`} className={counterClasses}>
        {charLimit - totalLength}
      </div>
    </>
  );
};
